import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { useNavigate } from "react-router-dom";
import { DeletePost, GetAllPost } from '../services/postservice';
import Spinner from '../components/Spinner'; // Import the common spinner

function PostList() {
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);

    const location = useLocation();
    const { successMessage } = location.state || {};
    const navigate = useNavigate();

    const totalPages = Math.ceil(totalCount / pageSize);

    useEffect(() => {
        fillData(page, pageSize);
        if (successMessage) toast(successMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize]);

    const fillData = async (currentPage = 1, currentPageSize = 50) => {
        try {
            setLoading(true);
            const response = await GetAllPost(0, currentPage, currentPageSize);
            setData(response.posts || []);
            setTotalCount(response.totalCount || 0);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching the data:', error);
            setLoading(false);
            toast.error('Failed to load posts');
        }
    };

    const handleDelete = async (postId) => {
        if (window.confirm("Are you sure you want to delete this post?")) {
            try {
                await DeletePost(postId);
                await fillData(page, pageSize);
                toast.success("Post deleted successfully!");
            } catch (error) {
                toast.error('Failed to delete post');
            }
        }
    };

    const handleEdit = (id) => {
        navigate('/post/add?pk=' + id);
    };

    const changePage = (p) => {
        if (p >= 1 && p <= totalPages) setPage(p);
    };

    const pageNumbers = () => {
        const pages = [];
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, page + 2);

        if (start > 1) pages.push(1, '...');
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < totalPages) pages.push('...', totalPages);

        return pages;
    };

    // Calculate range of items being shown
    const startItem = (page - 1) * pageSize + 1;
    const endItem = Math.min(page * pageSize, totalCount);

    return (
        <>
            {/* Full page overlay spinner when loading */}
            {loading && <Spinner overlay={true} text="Loading posts..." />}
            
            <div className="row">
                <div className="col-md-12 grid-margin stretch-card">
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <div className="d-flex justify-content-between mb-3">
                                <h5 className="card-title mb-0">Top Posts</h5>
                                <Link to="/post/add" className='nav-link'>
                                    <button type="button" className="btn btn-primary">New Post</button>
                                </Link>
                            </div>

                            {/* Table with loading state */}
                            <div className="table-responsive position-relative">
                                
                                <table className="table table-striped table-hover table-borderless align-middle">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>PK</th>
                                            <th>Media</th>
                                            <th>Title</th>
                                            <th>Author Name</th>
                                            <th>Views</th>
                                            <th>Read Time</th>
                                            <th>Category</th>
                                            <th>Posted On</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!loading && data.length ? data.map((item) => (
                                            <tr key={item.pk}>
                                                <td>{item.pk}</td>
                                                <td>
                                                    <img src={item.mediapath} alt={item.title} width="60" height="40" />
                                                </td>
                                                <td>{item.title}</td>
                                                <td>{item.authorname}</td>
                                                <td>{item.views}</td>
                                                <td>{item.readtime}</td>
                                                <td>{item.categoryname}</td>
                                                <td>{item.postedsortdate}</td>
                                                <td>
                                                    <i className="mdi mdi-tooltip-edit mdi-big text-primary me-2 pointer"
                                                        onClick={() => handleEdit(item.pk)}></i>
                                                    <i className="mdi mdi-delete mdi-big text-danger pointer"
                                                        onClick={() => handleDelete(item.pk)}></i>
                                                </td>
                                            </tr>
                                        )) : (
                                            !loading && (
                                                <tr>
                                                    <td colSpan="9" className="text-center">No posts found</td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Enhanced Pagination */}
                            <div className="d-flex justify-content-between align-items-center mt-4 p-3 bg-light rounded">
                                {/* Page Info */}
                                <div className="d-flex align-items-center">
                                    <span className="text-muted">
                                        Showing <strong>{startItem}-{endItem}</strong> of <strong>{totalCount}</strong> Records
                                    </span>
                                </div>

                                {/* Pagination Controls */}
                                <div className="d-flex align-items-center">
                                    {/* Previous Button */}
                                    <button
                                        className="btn btn-outline-primary btn-sm d-flex align-items-center me-2"
                                        onClick={() => changePage(page - 1)}
                                        disabled={page === 1 || loading}
                                    >
                                        <i className="mdi mdi-chevron-left me-1"></i>
                                        Previous
                                    </button>

                                    {/* Page Numbers */}
                                    <div className="btn-group mx-2" role="group">
                                        {pageNumbers().map((p, idx) =>
                                            p === '...' ? (
                                                <span key={idx} className="btn btn-outline-primary disabled">...</span>
                                            ) : (
                                                <button
                                                    key={idx}
                                                    className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-outline-primary'}`}
                                                    onClick={() => changePage(p)}
                                                    disabled={loading}
                                                >
                                                    {p}
                                                </button>
                                            )
                                        )}
                                    </div>

                                    {/* Next Button */}
                                    <button
                                        className="btn btn-outline-primary btn-sm d-flex align-items-center ms-2"
                                        onClick={() => changePage(page + 1)}
                                        disabled={page === totalPages || loading}
                                    >
                                        Next
                                        <i className="mdi mdi-chevron-right ms-1"></i>
                                    </button>
                                </div>

                                {/* Page Size Selector */}
                                <div className="d-flex align-items-center">
                                    <span className="text-muted small me-2">Show</span>
                                    <select
                                        value={pageSize}
                                        className="form-select form-select-sm border-primary shadow-sm"
                                        style={{ width: '70px' }}
                                        onChange={e => {
                                            setPageSize(Number(e.target.value));
                                            setPage(1);
                                        }}
                                        disabled={loading}
                                    >
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={75}>75</option>
                                    </select>
                                    <span className="text-muted small ms-2">Records</span>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
            <Toaster position="bottom-right" reverseOrder={false} />
        </>
    );
}

export default PostList;